
import { useState, useCallback, useEffect } from "react";
import { Tool } from "@/hooks/use-canvas-setup";
import { fetchGuideContent, fetchAllGuides } from "@/services/guide-service";

export type GuideStep = {
  id: string;
  title: string;
  description: string;
  tool?: Tool;
  checkCompletion?: (usedTools: any) => boolean;
  imageUrl?: string;
};

export type GuideType = {
  id: string;
  title: string;
  description: string;
  steps: GuideStep[];
};

// Helper functions for localStorage
const getStoredStepIndex = (scenarioId: string): number => {
  try {
    const stored = localStorage.getItem(`guide_step_${scenarioId}`);
    return stored ? parseInt(stored, 10) : 0;
  } catch (e) {
    console.error("Error retrieving step index from localStorage:", e);
    return 0;
  }
};

const setStoredStepIndex = (scenarioId: string, index: number): void => {
  try {
    localStorage.setItem(`guide_step_${scenarioId}`, index.toString());
  } catch (e) {
    console.error("Error storing step index in localStorage:", e);
  }
};

export const useGuideMode = (currentScenario: string | null) => {
  const [isGuideMode, setIsGuideMode] = useState(false);
  const [activeGuide, setActiveGuide] = useState<GuideType | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [guides, setGuides] = useState<Record<string, GuideType>>({});
  const [error, setError] = useState<string | null>(null);

  // Fetch all guides once on component mount
  useEffect(() => {
    const loadAllGuides = async () => {
      try {
        const allGuides = await fetchAllGuides();
        setGuides(allGuides);
      } catch (err) {
        console.error("Failed to load guides:", err);
        setError("Failed to load guides.");
      }
    };

    loadAllGuides();
  }, []);

  // Load the specific guide when currentScenario changes
  useEffect(() => {
    if (!currentScenario) return;
    
    const loadGuide = async () => {
      // First check if we already have this guide in our cache
      if (guides[currentScenario.toLowerCase()]) {
        setActiveGuide(guides[currentScenario.toLowerCase()]);
        // Load stored step index when guide loads
        setCurrentStepIndex(getStoredStepIndex(currentScenario.toLowerCase()));
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const guide = await fetchGuideContent(currentScenario.toLowerCase());
        
        if (guide) {
          setActiveGuide(guide);
          
          // Add to our cached guides
          setGuides(prev => ({
            ...prev,
            [currentScenario.toLowerCase()]: guide
          }));
          
          // Load stored step index when guide loads
          setCurrentStepIndex(getStoredStepIndex(currentScenario.toLowerCase()));
        } else {
          setError(`No guide found for ${currentScenario}`);
        }
      } catch (err) {
        console.error(`Failed to load guide for ${currentScenario}:`, err);
        setError(`Failed to load guide for ${currentScenario}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadGuide();
  }, [currentScenario, guides]);

  // Update localStorage whenever currentStepIndex or scenario changes
  useEffect(() => {
    if (currentScenario && activeGuide) {
      setStoredStepIndex(currentScenario.toLowerCase(), currentStepIndex);
    }
  }, [currentStepIndex, currentScenario, activeGuide]);

  const toggleGuideMode = useCallback(() => {
    if (isGuideMode) {
      // Exiting guide mode
      setIsGuideMode(false);
      // Do not reset step index when exiting guide mode
    } else {
      // Entering guide mode
      if (activeGuide) {
        setIsGuideMode(true);
        // We don't reset the step index here either, as we want to preserve it
      } else {
        console.error('No active guide available to display');
      }
    }
  }, [isGuideMode, activeGuide]);

  const nextStep = useCallback(() => {
    if (!activeGuide || !currentScenario) return false;
    
    if (currentStepIndex < activeGuide.steps.length - 1) {
      const newIndex = currentStepIndex + 1;
      setCurrentStepIndex(newIndex);
      setStoredStepIndex(currentScenario.toLowerCase(), newIndex);
      return true;
    }
    return false;
  }, [activeGuide, currentStepIndex, currentScenario]);

  const previousStep = useCallback(() => {
    if (!currentScenario) return false;
    
    if (currentStepIndex > 0) {
      const newIndex = currentStepIndex - 1;
      setCurrentStepIndex(newIndex);
      setStoredStepIndex(currentScenario.toLowerCase(), newIndex);
      return true;
    }
    return false;
  }, [currentStepIndex, currentScenario]);

  const resetGuide = useCallback(() => {
    setCurrentStepIndex(0);
    if (currentScenario) {
      setStoredStepIndex(currentScenario.toLowerCase(), 0);
    }
  }, [currentScenario]);

  const getCurrentStep = useCallback(() => {
    if (!activeGuide) return null;
    return activeGuide.steps[currentStepIndex];
  }, [activeGuide, currentStepIndex]);

  const getStepProgress = useCallback(() => {
    if (!activeGuide) return { current: 0, total: 0 };
    return { 
      current: currentStepIndex + 1, 
      total: activeGuide.steps.length 
    };
  }, [activeGuide, currentStepIndex]);

  const isLastStep = useCallback(() => {
    if (!activeGuide) return false;
    return currentStepIndex === activeGuide.steps.length - 1;
  }, [activeGuide, currentStepIndex]);

  const isFirstStep = useCallback(() => {
    return currentStepIndex === 0;
  }, [currentStepIndex]);

  return {
    isGuideMode,
    activeGuide,
    toggleGuideMode,
    nextStep,
    previousStep,
    resetGuide,
    getCurrentStep,
    getStepProgress,
    isLastStep,
    isFirstStep,
    isLoading,
    error,
    availableGuides: guides
  };
};
